/**
 * Slack Socket Mode ingest for the opencode runtime (ADR-0047).
 *
 * On Claude Code the slack-channel MCP owns the Socket Mode connection and pushes
 * inbound events straight into the session. opencode has no in-session channel
 * MCP, so the MANAGER runs the Socket Mode loop, gates each raw event with the
 * shared core SenderGate (`evaluateSlackInboundGate`), and drives the agent's
 * `opencode serve` over the inbound bridge on admit - the exact governance the
 * Claude handler applies, reused (no policy replicated here).
 *
 * The transport (apps.connections.open -> WebSocket -> ack) and the outbound send
 * (chat.postMessage) mirror slack-channel.ts. Both the socket factory and fetch
 * are injectable so the dispatch logic is unit-testable without a live Slack.
 *
 * Deliberately handles ONLY events_api message/app_mention envelopes. Slash
 * commands, block_actions and other interactive envelopes are acked and ignored
 * (opencode does not drive those through this path yet).
 */

import {
  evaluateSlackInboundGate,
  type SlackGateEvent,
} from '@augmented/core/channels/governance/slack-sender-gate.js';
import {
  isAppMentionEcho,
  slackInboundDedupId,
} from '@augmented/core/channels/governance/slack-inbound-filter.js';
import type { SenderPolicy } from '@augmented/core/channels/governance/slack-inbound-filter.js';
import type { SlackPeerClassifierConfig } from '@augmented/core/channels/governance/slack-peer-classifier.js';
import {
  buildSlackSenderPolicyFromEnv,
  buildSlackPeerClassifierConfigFromEnv,
  type EnvLike,
} from '@augmented/core/channels/governance/slack-inbound-config.js';
import type {
  InboundMessage,
  InboundResult,
} from '@augmented/core/provisioning/frameworks/opencode/index.js';

/**
 * Slack error codes meaning the bot token is DEFINITIVELY invalid, as opposed to
 * a transient network / 5xx blip. Mirrors `SLACK_AUTH_FAILURE_CODES` in
 * packages/mcp's slack-channel.ts; duplicated by hand because apps/cli must not
 * import packages/mcp (that would bundle baileys into the CLI).
 */
const SLACK_AUTH_FAILURE_CODES = new Set([
  'invalid_auth',
  'token_revoked',
  'token_expired',
  'account_inactive',
  'not_authed',
]);

/**
 * Resolve the bot's own Slack user id via `auth.test`, for self-echo and
 * app_mention-echo detection.
 *
 * `authFailed` separates a revoked/invalid token (stop retrying, surface it)
 * from a transient failure (worth another attempt). Never throws: an unresolved
 * id degrades echo filtering, which is far better than failing the whole channel
 * (the ENG-5592 lesson from the Claude path, where blocking startup on this call
 * left an agent permanently deaf on Slack).
 */
export async function resolveSlackBotUserId(
  botToken: string,
  fetchImpl?: FetchLike,
): Promise<{ id: string | undefined; authFailed: boolean }> {
  const doFetch = fetchImpl ?? (globalThis.fetch as unknown as FetchLike);
  try {
    const res = await doFetch(`${SLACK_API}/auth.test`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${botToken}` },
      signal: AbortSignal.timeout(10_000),
    });
    const data = (await res.json()) as { ok?: boolean; user_id?: string; error?: string };
    if (data.ok) return { id: data.user_id, authFailed: false };
    return { id: undefined, authFailed: !!data.error && SLACK_AUTH_FAILURE_CODES.has(data.error) };
  } catch {
    // Network / parse failure - transient, NOT a definitive auth failure.
    return { id: undefined, authFailed: false };
  }
}

/**
 * The Slack gate + transport parameters an opencode agent's ingest needs,
 * derived from its materialized Slack env (Brad's option (a): the manager
 * materializes the same env contract the Claude MCP uses via
 * `buildChannelCredentialEnv` + `buildChannelServerEnv`, and this turns it into
 * ingest params). `botUserId` is NOT here - it is not env-derivable and is
 * resolved separately via `resolveSlackBotUserId`.
 */
export interface SlackIngestParams {
  appToken: string;
  botToken: string;
  senderPolicy: SenderPolicy;
  peerConfig: SlackPeerClassifierConfig;
  /** Emoji added on admit ("seen, working on it"); from SLACK_ACK_REACTION. */
  ackReaction?: string;
  /** Emoji added on an admitted no-reply ("seen, no action"); from SLACK_SKIP_REACTION. */
  skipReaction?: string;
}

/**
 * Resolve the ingest's gate + transport params from a materialized Slack env.
 * Returns null when Socket Mode ingest is impossible (no app token or no bot
 * token) so the caller simply skips starting the loop. Fail-closed policy
 * parsing (a misconfigured sender policy) propagates as a throw, exactly as the
 * Claude MCP boot does.
 */
export function resolveSlackIngestParamsFromEnv(
  env: EnvLike,
  ctx: { agentTeamId: string | null },
): SlackIngestParams | null {
  const appToken = env['SLACK_APP_TOKEN'];
  const botToken = env['SLACK_BOT_TOKEN'];
  if (!appToken || !botToken) return null;
  const ackReaction = (env['SLACK_ACK_REACTION'] ?? '').trim() || undefined;
  const skipReaction = (env['SLACK_SKIP_REACTION'] ?? '').trim() || undefined;
  return {
    appToken,
    botToken,
    senderPolicy: buildSlackSenderPolicyFromEnv(env, ctx),
    peerConfig: buildSlackPeerClassifierConfigFromEnv(env),
    ...(ackReaction ? { ackReaction } : {}),
    ...(skipReaction ? { skipReaction } : {}),
  };
}

/** Minimal WebSocket surface (the global `WebSocket` satisfies it). */
export interface SlackWebSocketLike {
  send(data: string): void;
  close(): void;
  onopen: ((ev?: unknown) => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev?: unknown) => void) | null;
  onerror: ((ev?: unknown) => void) | null;
}
export type SlackSocketFactory = (url: string) => SlackWebSocketLike;

/** Minimal fetch surface (avoids a DOM-lib dependency). */
export interface MinimalJsonResponse {
  ok: boolean;
  status: number;
  json(): Promise<unknown>;
}
export type FetchLike = (
  url: string,
  init?: { method?: string; headers?: Record<string, string>; body?: string; signal?: AbortSignal },
) => Promise<MinimalJsonResponse>;

/** The inner Slack event carried by an events_api envelope. */
export type SlackEventsApiEvent = SlackGateEvent & { ts?: string };

export type SlackReplySender = (args: {
  channel: string;
  text: string;
  threadTs?: string;
}) => Promise<void>;

export interface SlackIngestConfig {
  codeName: string;
  agentId: string;
  /** App-Level Token for Socket Mode (`xapp-...`). */
  appToken: string;
  /** Bot token for chat.postMessage (`xoxb-...`). */
  botToken: string;
  /** The agent bot's own Slack user id (auth.test `user_id`), for echo/self detection. */
  botUserId: string | undefined;
  senderPolicy: SenderPolicy;
  peerConfig: SlackPeerClassifierConfig;
  /** Inject an admitted, normalized message into the agent's opencode server. */
  inject: (msg: InboundMessage) => Promise<InboundResult>;
  log: (m: string) => void;
  /** Emoji added on admit ("seen"); from SLACK_ACK_REACTION. Omitted → no ack. */
  ackReaction?: string;
  /** Emoji added on an admitted no-reply; from SLACK_SKIP_REACTION. Omitted → none. */
  skipReaction?: string;
  /** Test seams. */
  fetchImpl?: FetchLike;
  socketFactory?: SlackSocketFactory;
  sendReply?: SlackReplySender;
  addReaction?: SlackReactionAdder;
  removeReaction?: SlackReactionRemover;
}

/** Adds an emoji reaction to a Slack message (fire-and-forget; failures are logged, never thrown). */
export type SlackReactionAdder = (args: { channel: string; timestamp: string; name: string }) => Promise<void>;

/** Removes an emoji reaction from a Slack message (fire-and-forget; failures are logged, never thrown). */
export type SlackReactionRemover = (args: { channel: string; timestamp: string; name: string }) => Promise<void>;

/** What the dispatcher decided for one inbound event (returned for tests/telemetry). */
export type SlackDispatchOutcome =
  | 'dropped_echo'
  | 'dropped_duplicate'
  | 'dropped_gate'
  | 'declined'
  | 'admitted_no_reply'
  | 'admitted_replied';

interface DispatchCtx {
  codeName: string;
  botUserId: string | undefined;
  senderPolicy: SenderPolicy;
  peerConfig: SlackPeerClassifierConfig;
  inject: (msg: InboundMessage) => Promise<InboundResult>;
  sendReply: SlackReplySender;
  addReaction: SlackReactionAdder;
  removeReaction: SlackReactionRemover;
  ackReaction?: string;
  skipReaction?: string;
  log: (m: string) => void;
  /** Bounded channel:ts dedup for Socket Mode reconnect redelivery. */
  seen: BoundedSet;
}

/** Insertion-ordered set with a hard cap so redelivery dedup can't grow unbounded. */
export class BoundedSet {
  private readonly max: number;
  private readonly set = new Set<string>();
  constructor(max = 500) {
    this.max = max;
  }
  has(k: string): boolean {
    return this.set.has(k);
  }
  add(k: string): void {
    this.set.add(k);
    if (this.set.size > this.max) {
      const oldest = this.set.values().next().value as string | undefined;
      if (oldest !== undefined) this.set.delete(oldest);
    }
  }
}

/**
 * Dispatch one Slack events_api event: echo-dedup -> reconnect-dedup -> gate ->
 * inject -> reply. Pure of transport (no socket, no ack). Returns the outcome.
 */
export async function dispatchSlackInbound(
  event: SlackEventsApiEvent,
  ctx: DispatchCtx,
): Promise<SlackDispatchOutcome> {
  // 1. Drop the `message` echo of an `app_mention` (Slack fires both for one
  //    mention, sharing a ts). The app_mention is the canonical delivery.
  if (isAppMentionEcho(event, ctx.botUserId ?? null)) {
    return 'dropped_echo';
  }

  // 2. Reconnect redelivery: Slack resends unacked events on a new socket. Drop
  //    an event id we have already processed this process-life.
  //    ENG-8387: the id is namespaced by event TYPE, not bare `channel:ts` — an
  //    app_mention and its `message` echo share a ts, so the old key let the
  //    echo claim the mention's slot whenever step 1 failed open on an
  //    unresolved botUserId (the pre-connect window after a restart), and the
  //    actionable app_mention was then dropped here as a "duplicate". Same-type
  //    redelivery — the case this guard exists for — is unaffected.
  const dedupKey = slackInboundDedupId(event);
  if (dedupKey) {
    if (ctx.seen.has(dedupKey)) return 'dropped_duplicate';
    ctx.seen.add(dedupKey);
  }

  // 3. Governance gate (content filter, sender policy, peer classification).
  const decision = evaluateSlackInboundGate(event, {
    senderPolicy: ctx.senderPolicy,
    peerConfig: ctx.peerConfig,
    botUserId: ctx.botUserId,
  });
  if (!decision.admit) {
    ctx.log(`[slack-ingest:${ctx.codeName}] dropped ${redact(event.channel)} reason=${decision.reason}`);
    return 'dropped_gate';
  }

  // Admitted: acknowledge receipt with the "seen" reaction. Fire-and-forget so
  // it never blocks the inject or fails the turn (ENG-7840 parity with the
  // claude slack-channel MCP; opencode has no in-session MCP, so the manager
  // owns the reaction).
  // Retain the ack-add promise so the no-reply skip path below can sequence its
  // ack-removal AFTER the add resolves. Without that, a slow add can land after
  // the remove has already fired (missing it), leaving the ack next to the skip
  // — the exact "shows BOTH" bug this reaction dance exists to avoid.
  let ackReactionInFlight: Promise<unknown> = Promise.resolve();
  if (ctx.ackReaction && event.channel && event.ts) {
    ackReactionInFlight = ctx.addReaction({ channel: event.channel, timestamp: event.ts, name: ctx.ackReaction });
    // The reply/return paths don't await it; swallow a rejection there so a
    // failed best-effort ack can't surface as an unhandled rejection.
    ackReactionInFlight.catch(() => {});
  }

  // 4. Normalize + inject. A thread reply maps to a stable conversation key so
  //    the opencode session keeps that thread's context across turns.
  const conversationKey = `${event.channel ?? 'nochannel'}:${event.thread_ts ?? event.ts ?? ''}`;
  const meta: Record<string, string> = {
    source: 'slack',
    lane: 'conversational',
    requires_reply: 'true',
  };
  if (event.thread_ts) meta['thread_ts'] = event.thread_ts;
  else if (event.ts) meta['thread_ts'] = event.ts;

  const result = await ctx.inject({
    channelId: 'slack',
    conversationKey,
    senderId: event.user ?? 'unknown',
    text: event.text ?? '',
    meta,
  });

  if (result.status === 'declined') {
    ctx.log(`[slack-ingest:${ctx.codeName}] inject declined reason=${result.reason}`);
    return 'declined';
  }
  // 5. Post the reply back in-thread.
  if (result.status === 'replied' && result.reply && event.channel) {
    await ctx.sendReply({
      channel: event.channel,
      text: result.reply,
      threadTs: event.thread_ts ?? event.ts,
    });
    return 'admitted_replied';
  }
  // Admitted but the agent produced no reply: mark it "seen, no action". ENG-6662:
  // the skip reaction RETRACTS the ack — Slack reactions are additive, so remove
  // the earlier "working" ack first or the message shows BOTH the ack and skip.
  if (ctx.skipReaction && event.channel && event.ts) {
    const channel = event.channel;
    const timestamp = event.ts;
    const { ackReaction, skipReaction } = ctx;
    // Same emoji for ack and skip: the ack already stands as the terminal
    // "seen" marker, so removing then re-adding it is pointless churn (and would
    // race itself) — leave it in place.
    if (ackReaction !== skipReaction) {
      // Serialize ack-settle -> remove-ack -> add-skip so the retract is
      // deterministic (remove can't outrun the add, add-skip can't precede the
      // remove). Fire-and-forget at this boundary — best-effort, never blocks
      // the turn; the ordering is what matters, not awaiting here.
      void (async () => {
        try {
          if (ackReaction) {
            await ackReactionInFlight;
            await ctx.removeReaction({ channel, timestamp, name: ackReaction });
          }
          await ctx.addReaction({ channel, timestamp, name: skipReaction });
        } catch {
          /* reaction bookkeeping is best-effort */
        }
      })();
    }
  }
  return 'admitted_no_reply';
}

/** Redact a Slack id for durable logs (keep the leading type char). */
function redact(id: string | undefined): string {
  if (!id) return '?';
  return `${id.slice(0, 1)}***`;
}

/** Handle to a running ingest connection. */
export interface SlackIngestHandle {
  stop(): void;
}

const SLACK_API = 'https://slack.com/api';

/** Default outbound: chat.postMessage via the bot token. */
function defaultSendReply(botToken: string, fetchImpl: FetchLike, log: (m: string) => void): SlackReplySender {
  return async ({ channel, text, threadTs }) => {
    try {
      const res = await fetchImpl(`${SLACK_API}/chat.postMessage`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${botToken}`,
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({ channel, text, ...(threadTs ? { thread_ts: threadTs } : {}) }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      if (!data.ok) log(`[slack-ingest] chat.postMessage failed: ${data.error}`);
    } catch (err) {
      log(`[slack-ingest] chat.postMessage threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
}

/** Default reaction: reactions.add via the bot token. Fire-and-forget. */
function defaultAddReaction(botToken: string, fetchImpl: FetchLike, log: (m: string) => void): SlackReactionAdder {
  return async ({ channel, timestamp, name }) => {
    try {
      const res = await fetchImpl(`${SLACK_API}/reactions.add`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${botToken}`,
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({ channel, timestamp, name }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      // already_reacted is benign (a reconnect redelivery re-adds the same ack);
      // don't log it as a failure.
      if (!data.ok && data.error !== 'already_reacted') {
        log(`[slack-ingest] reactions.add failed: ${data.error}`);
      }
    } catch (err) {
      log(`[slack-ingest] reactions.add threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
}

/** Default reaction removal: reactions.remove via the bot token. Fire-and-forget. */
function defaultRemoveReaction(botToken: string, fetchImpl: FetchLike, log: (m: string) => void): SlackReactionRemover {
  return async ({ channel, timestamp, name }) => {
    try {
      const res = await fetchImpl(`${SLACK_API}/reactions.remove`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${botToken}`,
          'Content-Type': 'application/json; charset=utf-8',
        },
        body: JSON.stringify({ channel, timestamp, name }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };
      // no_reaction is benign (the ack never landed, or was already cleared);
      // don't log it as a failure.
      if (!data.ok && data.error !== 'no_reaction') {
        log(`[slack-ingest] reactions.remove failed: ${data.error}`);
      }
    } catch (err) {
      log(`[slack-ingest] reactions.remove threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
}

/**
 * Start the Socket Mode ingest loop for one opencode agent. Opens an
 * apps.connections.open socket, acks + dispatches events_api envelopes, and
 * reconnects on close/disconnect until stopped.
 */
export function startSlackIngest(config: SlackIngestConfig): SlackIngestHandle {
  const fetchImpl = config.fetchImpl ?? (globalThis.fetch as unknown as FetchLike);
  const socketFactory =
    config.socketFactory ??
    ((url: string) => {
      const Ctor = (globalThis as { WebSocket?: new (u: string) => SlackWebSocketLike }).WebSocket;
      if (!Ctor) throw new Error('global WebSocket is unavailable (Node 22+ required for opencode Slack ingest)');
      return new Ctor(url);
    });
  const sendReply = config.sendReply ?? defaultSendReply(config.botToken, fetchImpl, config.log);
  const addReaction = config.addReaction ?? defaultAddReaction(config.botToken, fetchImpl, config.log);
  const removeReaction = config.removeReaction ?? defaultRemoveReaction(config.botToken, fetchImpl, config.log);
  const seen = new BoundedSet();

  let stopped = false;
  let ws: SlackWebSocketLike | null = null;
  const ctx: DispatchCtx = {
    codeName: config.codeName,
    botUserId: config.botUserId,
    senderPolicy: config.senderPolicy,
    peerConfig: config.peerConfig,
    inject: config.inject,
    sendReply,
    addReaction,
    removeReaction,
    ...(config.ackReaction ? { ackReaction: config.ackReaction } : {}),
    ...(config.skipReaction ? { skipReaction: config.skipReaction } : {}),
    log: config.log,
    seen,
  };

  const scheduleReconnect = (delayMs: number) => {
    if (stopped) return;
    setTimeout(() => void connect(), delayMs).unref?.();
  };

  const connect = async (): Promise<void> => {
    if (stopped) return;
    let url: string | undefined;
    try {
      const res = await fetchImpl(`${SLACK_API}/apps.connections.open`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${config.appToken}` },
        // Bound the open call so a hung request can't wedge ingest without a
        // reconnect (Node 22+ required for opencode Slack ingest, so
        // AbortSignal.timeout is available).
        signal: AbortSignal.timeout(10_000),
      });
      const data = (await res.json()) as { ok: boolean; url?: string; error?: string };
      if (!data.ok || !data.url) {
        config.log(`[slack-ingest:${config.codeName}] apps.connections.open failed: ${data.error}`);
        scheduleReconnect(10_000);
        return;
      }
      url = data.url;
    } catch (err) {
      config.log(`[slack-ingest:${config.codeName}] connect threw: ${err instanceof Error ? err.message : String(err)}`);
      scheduleReconnect(10_000);
      return;
    }
    if (stopped) return;

    // Socket construction can throw (no global WebSocket, ctor failure). Keep it
    // inside a try so a throw schedules a reconnect instead of escaping connect()
    // as an unhandled rejection that leaves ingest permanently wedged.
    try {
      ws = socketFactory(url);
      ws.onopen = () => config.log(`[slack-ingest:${config.codeName}] Socket Mode connected`);
      ws.onclose = () => {
        if (!stopped) scheduleReconnect(5_000);
      };
      ws.onerror = () => {
        /* onclose follows; reconnect handled there */
      };
      ws.onmessage = (ev) => {
        void onEnvelope(String(ev.data));
      };
    } catch (err) {
      config.log(`[slack-ingest:${config.codeName}] socket construction threw: ${err instanceof Error ? err.message : String(err)}`);
      scheduleReconnect(10_000);
    }
  };

  const onEnvelope = async (raw: string): Promise<void> => {
    let msg: SlackEnvelope;
    try {
      msg = JSON.parse(raw) as SlackEnvelope;
    } catch {
      return;
    }
    // Ack every envelope immediately (Slack retries an unacked one, and drops
    // the socket if the ack misses its ~3s window). `inject` runs a full
    // synchronous agent turn, so we cannot defer the ack behind it. This is a
    // deliberate at-most-once boundary: a crash between ack and inject loses the
    // event. Durable at-least-once delivery (persist -> ack -> process from a
    // queue) is deferred to the opencode ingest live-validation pass.
    if (msg.envelope_id && ws) {
      try {
        ws.send(JSON.stringify({ envelope_id: msg.envelope_id }));
      } catch {
        /* socket died; reconnect will follow */
      }
    }
    if (msg.type === 'disconnect') {
      // Slack asks us to reconnect (socket reap / refresh). Close and reopen.
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      return;
    }
    if (msg.type !== 'events_api') return;
    const event = msg.payload?.event;
    if (!event || (event.type !== 'message' && event.type !== 'app_mention')) return;
    try {
      await dispatchSlackInbound(event as SlackEventsApiEvent, ctx);
    } catch (err) {
      config.log(`[slack-ingest:${config.codeName}] dispatch threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  void connect();

  return {
    stop() {
      stopped = true;
      try {
        ws?.close();
      } catch {
        /* ignore */
      }
      ws = null;
    },
  };
}

interface SlackEnvelope {
  type: string;
  envelope_id?: string;
  payload?: { event?: SlackEventsApiEvent & { type?: string } };
}
